import {
  QueryClient,
  useMutation,
  type UseMutationOptions,
} from '@tanstack/react-query'
import { produce } from 'immer'
import {
  stories,
  type OpalApiRequestBody,
  type OpalApiResponseDocument,
} from '@opallabs/api/v3'
import {
  type ReadableAccessMetadata,
  type WritableAccessMetadata,
} from '@opallabs/concepts'
import { globalQueryKeys } from '../../queryKeys'

type UpdateOperation = 'UpdateStoryV3'
type ResponseBody = OpalApiResponseDocument<UpdateOperation, 200>

type RequestData = OpalApiRequestBody<UpdateOperation>['data']
type MutationParams = RequestData['attributes'] & {
  access: WritableAccessMetadata
  id: RequestData['id']
}

type HookOptions = Pick<
  UseMutationOptions<ResponseBody, unknown, MutationParams>,
  'onMutate' | 'onSuccess'
>

export function transformStoryUsingRequest({
  access,
  cachedGroupsData,
  cachedStoryData,
}: {
  access: WritableAccessMetadata
  cachedGroupsData?: OpalApiResponseDocument<'ReadUserGroupsV3', 200>['data']
  cachedStoryData?: ResponseBody
}) {
  if (!cachedStoryData) return

  return produce(cachedStoryData, (draftDocument) => {
    if (draftDocument?.meta?.access) {
      draftDocument.meta.access.users = access.users ?? []

      draftDocument.meta.access.groups =
        access.groups?.reduce<ReadableAccessMetadata['groups']>(
          (groups, group) => {
            const cachedGroup = cachedGroupsData?.find((g) => g.id === group.id)
            if (cachedGroup) {
              groups.push({
                type: 'user_group',
                id: group.id,
                meta: {
                  title: cachedGroup.attributes.name,
                  count: cachedGroup.relationships.members.data.length,
                },
              })
            }

            return groups
          },
          []
        ) ?? []

      draftDocument.meta.access.resources.forEach((resource) => {
        const inheritsMembership = access.resources?.find(
          (r) => r.id === resource.id
        )?.meta.inherits_membership

        if (inheritsMembership) {
          resource.meta.inherits_membership = inheritsMembership
        }
      })
    }

    return draftDocument
  })
}

/**
 * Call this within `useUpdateStoryAccess`'s `onMutate` prop to update the `react-query` cache for a given story.
 * Should be used together with `updateStoryForPersistedAccess` to update the cache to the final state.
 *
 * @example
 * const queryClient = useQueryClient()
 * // ...
 * useUpdateStoryAccess({
 *   onMutate: (mutationParams) => {
 *     optimisticallyUpdateStoryForAccess(queryClient, mutationParams)
 *   },
 * })
 */
export function optimisticallyUpdateStoryForAccess(
  queryClient: QueryClient,
  params: MutationParams
) {
  const { id, access } = params
  const { data: groupData } =
    queryClient.getQueryData<OpalApiResponseDocument<'ReadUserGroupsV3', 200>>(
      globalQueryKeys.groups(undefined, ['asset', 'members'])
    ) ?? {}

  queryClient.setQueryData<ResponseBody>(
    globalQueryKeys.storyAccess(id),
    (cachedData) =>
      transformStoryUsingRequest({
        access,
        cachedGroupsData: groupData,
        cachedStoryData: cachedData,
      })
  )
}

/** @private Only exported for tests */
export function transformStoryUsingResponse(
  response: ResponseBody,
  cachedStory?: ResponseBody
): ResponseBody {
  if (!cachedStory) return response

  return produce(cachedStory, (draftDocument) => {
    if (draftDocument?.meta?.access) {
      draftDocument.meta.access = response.meta?.access
    }

    if (draftDocument?.data?.meta?.effective_privacy) {
      draftDocument.data.meta.effective_privacy =
        response.data.meta?.effective_privacy
    }

    return draftDocument
  }) satisfies ResponseBody
}

/**
 * Call this within `useUpdateStoryAccess`'s `onSuccess` prop to update the `react-query` cache for a given story.
 *
 * @example
 * const queryClient = useQueryClient()
 * // ...
 * useUpdateStoryAccess({
 *   onSuccess: (data) => {
 *     updateStoryForPersistedAccess(queryClient, data)
 *   },
 * })
 */
export function updateStoryForPersistedAccess(
  queryClient: QueryClient,
  data: ResponseBody
) {
  const id = data.data.id
  const queryKey = globalQueryKeys.storyAccess(id)

  // If we have the meta.access structure in the response,
  // use it to update the cache instead of invalidating
  if (data.meta?.access != null) {
    queryClient.setQueryData<ResponseBody>(queryKey, (cachedData) =>
      transformStoryUsingResponse(data, cachedData)
    )
  } else {
    queryClient.invalidateQueries(queryKey)
  }
}

export const useUpdateStoryAccess = ({ onMutate, onSuccess }: HookOptions) =>
  useMutation({
    mutationFn: async (params) => {
      const { access, id } = params

      return await stories.update({
        params: {
          path: {
            story_id: id,
          },
          query: {
            expose: {
              meta: {
                access: 'true',
              },
            },
          },
        },
        requestBody: {
          data: {
            id,
            type: 'story',
          },
          meta: {
            access,
          },
        },
      })
    },
    onMutate,
    onSuccess,
  })
